import {
  FIELD_MAPPINGS,
  MAPPING_LABELS,
  STATIC_FIELD_TYPES,
  SUBMIT,
  type ConditionGroup,
  type FieldType,
  type FormDefinition,
  type FormField,
  type FormPage,
} from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlignLeft,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Calendar,
  CheckSquare,
  ChevronDown,
  CircleDot,
  Code2,
  Copy,
  Eye,
  GitBranch,
  GripVertical,
  Hash,
  Heading,
  History,
  ListChecks,
  ListFilter,
  Mail,
  Paperclip,
  Phone,
  Plus,
  Settings,
  Text,
  Trash2,
  Type,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FormRenderer } from '@/components/form-renderer';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, Input, Label, Select, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import type { AreaConfig, FormDetail } from '@/lib/types';
import { cn, formatDateTime } from '@/lib/utils';
import { ACCESS_LABELS, EmbedDialog } from './forms-page';

const PALETTE: { type: FieldType; label: string; icon: LucideIcon }[] = [
  { type: 'text', label: 'Textzeile', icon: Type },
  { type: 'textarea', label: 'Textbereich', icon: AlignLeft },
  { type: 'email', label: 'E-Mail', icon: Mail },
  { type: 'phone', label: 'Telefon', icon: Phone },
  { type: 'number', label: 'Zahl', icon: Hash },
  { type: 'date', label: 'Datum', icon: Calendar },
  { type: 'select', label: 'Auswahlliste', icon: ListFilter },
  { type: 'radio', label: 'Optionen', icon: CircleDot },
  { type: 'checkboxes', label: 'Mehrfachauswahl', icon: ListChecks },
  { type: 'checkbox', label: 'Bestätigung', icon: CheckSquare },
  { type: 'file', label: 'Datei / Foto', icon: Paperclip },
  { type: 'heading', label: 'Überschrift', icon: Heading },
  { type: 'paragraph', label: 'Hinweistext', icon: Text },
];
const TYPE_LABEL = Object.fromEntries(PALETTE.map((p) => [p.type, p.label])) as Record<FieldType, string>;
const TYPE_ICON = Object.fromEntries(PALETTE.map((p) => [p.type, p.icon])) as Record<FieldType, LucideIcon>;
const OPS = [
  { value: 'equals', label: 'ist' },
  { value: 'notEquals', label: 'ist nicht' },
  { value: 'contains', label: 'enthält' },
  { value: 'notContains', label: 'enthält nicht' },
  { value: 'notEmpty', label: 'ist ausgefüllt' },
  { value: 'isEmpty', label: 'ist leer' },
] as const;

const rid = (p: string) => `${p}${Math.random().toString(36).slice(2, 9)}`;

function toKey(label: string, taken: Set<string>) {
  let base =
    label
      .toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'feld';
  if (!/^[a-z]/.test(base)) base = `f_${base}`;
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
  return key;
}

function newField(type: FieldType, taken: Set<string>): FormField {
  const label = type === 'heading' ? 'Überschrift' : type === 'paragraph' ? 'Hinweistext' : TYPE_LABEL[type];
  return {
    id: rid('f_'),
    key: toKey(label, taken),
    type,
    label,
    required: false,
    width: 'full',
    ...(['select', 'radio', 'checkboxes'].includes(type) ? { options: [{ value: 'option_1', label: 'Option 1' }, { value: 'option_2', label: 'Option 2' }] } : {}),
  };
}

// --- Bedingungs-Editor ------------------------------------------------------
function ConditionEditor({ value, onChange, fields }: { value: ConditionGroup | undefined; onChange: (v: ConditionGroup | undefined) => void; fields: FormField[] }) {
  if (!value) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={fields.length === 0}
        onClick={() => onChange({ mode: 'all', conditions: [{ field: fields[0]!.key, op: 'equals', value: fields[0]!.options?.[0]?.value ?? '' }] })}
      >
        <Plus /> Bedingung
      </Button>
    );
  }
  const update = (i: number, patch: Partial<ConditionGroup['conditions'][number]>) =>
    onChange({ ...value, conditions: value.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <div className="grid gap-2 rounded-md border p-2">
      {value.conditions.length > 1 && (
        <Select className="h-8" value={value.mode} onChange={(e) => onChange({ ...value, mode: e.target.value as 'all' | 'any' })} aria-label="Verknüpfung">
          <option value="all">alle Bedingungen erfüllt</option>
          <option value="any">mindestens eine erfüllt</option>
        </Select>
      )}
      {value.conditions.map((c, i) => {
        const f = fields.find((x) => x.key === c.field);
        const needsValue = c.op !== 'isEmpty' && c.op !== 'notEmpty';
        return (
          <div key={i} className="grid gap-1.5 rounded bg-muted/40 p-2">
            <div className="flex gap-1.5">
              <Select className="h-8 flex-1" value={c.field} onChange={(e) => update(i, { field: e.target.value, value: fields.find((x) => x.key === e.target.value)?.options?.[0]?.value ?? '' })} aria-label="Feld">
                {fields.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.label}
                  </option>
                ))}
              </Select>
              <Button type="button" size="icon" variant="ghost" className="size-8" onClick={() => {
                const rest = value.conditions.filter((_, j) => j !== i);
                onChange(rest.length ? { ...value, conditions: rest } : undefined);
              }} aria-label="Bedingung entfernen">
                <Trash2 />
              </Button>
            </div>
            <div className="flex gap-1.5">
              <Select className="h-8 w-36" value={c.op} onChange={(e) => update(i, { op: e.target.value as typeof c.op })} aria-label="Vergleich">
                {OPS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              {needsValue &&
                (f?.options ? (
                  <Select className="h-8 flex-1" value={c.value ?? ''} onChange={(e) => update(i, { value: e.target.value })} aria-label="Wert">
                    {f.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input className="h-8 flex-1" value={c.value ?? ''} onChange={(e) => update(i, { value: e.target.value })} aria-label="Wert" />
                ))}
            </div>
          </div>
        );
      })}
      <Button type="button" size="sm" variant="ghost" className="w-fit" onClick={() => onChange({ ...value, conditions: [...value.conditions, { field: fields[0]!.key, op: 'notEmpty' }] })}>
        <Plus /> weitere Bedingung
      </Button>
    </div>
  );
}

// --- Eigenschaften ----------------------------------------------------------
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid gap-3 border-t pt-4 first:border-0 first:pt-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

function FieldProperties({ field, onChange, conditionFields, allKeys }: { field: FormField; onChange: (f: FormField) => void; conditionFields: FormField[]; allKeys: Set<string> }) {
  const set = <K extends keyof FormField>(k: K, v: FormField[K]) => onChange({ ...field, [k]: v });
  const isStatic = STATIC_FIELD_TYPES.includes(field.type);
  const hasOptions = ['select', 'radio', 'checkboxes'].includes(field.type);
  const keyConflict = !isStatic && allKeys.has(field.key);
  const validation = field.validation ?? {};
  const setVal = (patch: Partial<NonNullable<FormField['validation']>>) => {
    const next = Object.fromEntries(Object.entries({ ...validation, ...patch }).filter(([, v]) => v !== undefined && v !== '' && !Number.isNaN(v)));
    onChange({ ...field, validation: Object.keys(next).length ? next : undefined });
  };
  const num = (v: string) => (v === '' ? undefined : Number(v));

  return (
    <div className="grid gap-4">
      <Section title={TYPE_LABEL[field.type]}>
        <Field label={isStatic ? 'Text' : 'Beschriftung'} htmlFor="p-label">
          {field.type === 'paragraph' ? (
            <Textarea id="p-label" rows={4} value={field.label} onChange={(e) => set('label', e.target.value)} />
          ) : (
            <Input id="p-label" value={field.label} onChange={(e) => set('label', e.target.value)} />
          )}
        </Field>
        {!isStatic && (
          <>
            <Field label="Hilfetext" htmlFor="p-help">
              <Input id="p-help" value={field.help ?? ''} onChange={(e) => set('help', e.target.value || undefined)} />
            </Field>
            {['text', 'textarea', 'email', 'phone', 'number'].includes(field.type) && (
              <Field label="Platzhalter" htmlFor="p-ph">
                <Input id="p-ph" value={field.placeholder ?? ''} onChange={(e) => set('placeholder', e.target.value || undefined)} />
              </Field>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={field.required} onCheckedChange={(v) => set('required', v)} /> Pflichtfeld
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={field.width === 'half'} onCheckedChange={(v) => set('width', v ? 'half' : 'full')} /> halbe Breite
            </label>
          </>
        )}
      </Section>

      {hasOptions && (
        <Section title="Auswahlmöglichkeiten">
          {(field.options ?? []).map((o, i) => (
            <div key={i} className="flex gap-1.5">
              <Input
                className="h-8"
                value={o.label}
                onChange={(e) => {
                  const options = [...(field.options ?? [])];
                  const taken = new Set(options.filter((_, j) => j !== i).map((x) => x.value));
                  options[i] = { label: e.target.value, value: o.value.startsWith('option_') ? toKey(e.target.value, taken) : o.value };
                  set('options', options);
                }}
                aria-label={`Option ${i + 1}`}
              />
              <Button type="button" size="icon" variant="ghost" className="size-8" onClick={() => set('options', (field.options ?? []).filter((_, j) => j !== i))} aria-label="Option entfernen">
                <Trash2 />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-fit"
            onClick={() => {
              const taken = new Set((field.options ?? []).map((o) => o.value));
              set('options', [...(field.options ?? []), { value: toKey(`option ${(field.options?.length ?? 0) + 1}`, taken), label: `Option ${(field.options?.length ?? 0) + 1}` }]);
            }}
          >
            <Plus /> Option
          </Button>
        </Section>
      )}

      {!isStatic && ['text', 'textarea', 'phone'].includes(field.type) && (
        <Section title="Prüfung">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Min. Zeichen" htmlFor="v-min">
              <Input id="v-min" type="number" min={0} value={validation.minLength ?? ''} onChange={(e) => setVal({ minLength: num(e.target.value) })} />
            </Field>
            <Field label="Max. Zeichen" htmlFor="v-max">
              <Input id="v-max" type="number" min={1} value={validation.maxLength ?? ''} onChange={(e) => setVal({ maxLength: num(e.target.value) })} />
            </Field>
          </div>
          {field.type === 'text' && (
            <>
              <Field label="Muster (regulärer Ausdruck)" htmlFor="v-pat" hint="z. B. [0-9]{5} für eine PLZ">
                <Input id="v-pat" className="font-mono" value={validation.pattern ?? ''} onChange={(e) => setVal({ pattern: e.target.value || undefined })} />
              </Field>
              {validation.pattern && (
                <Field label="Fehlermeldung bei falschem Format" htmlFor="v-patmsg">
                  <Input id="v-patmsg" value={validation.patternMessage ?? ''} onChange={(e) => setVal({ patternMessage: e.target.value || undefined })} />
                </Field>
              )}
            </>
          )}
        </Section>
      )}
      {field.type === 'number' && (
        <Section title="Prüfung">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Minimum" htmlFor="v-nmin">
              <Input id="v-nmin" type="number" value={validation.min ?? ''} onChange={(e) => setVal({ min: num(e.target.value) })} />
            </Field>
            <Field label="Maximum" htmlFor="v-nmax">
              <Input id="v-nmax" type="number" value={validation.max ?? ''} onChange={(e) => setVal({ max: num(e.target.value) })} />
            </Field>
          </div>
        </Section>
      )}
      {field.type === 'file' && (
        <Section title="Dateien">
          <Field label="Maximale Anzahl" htmlFor="v-files">
            <Input id="v-files" type="number" min={1} max={10} value={validation.maxFiles ?? 5} onChange={(e) => setVal({ maxFiles: num(e.target.value) })} />
          </Field>
        </Section>
      )}

      <Section title="Sichtbarkeit">
        <p className="text-xs text-muted-foreground">Nur anzeigen, wenn …</p>
        <ConditionEditor value={field.visibleIf} onChange={(v) => set('visibleIf', v)} fields={conditionFields.filter((f) => f.key !== field.key)} />
      </Section>

      {!isStatic && (
        <Section title="Daten">
          <Field label="Feldschlüssel" htmlFor="p-key" hint="Technischer Name, z. B. für Bedingungen" error={keyConflict ? 'Schlüssel bereits vergeben' : undefined}>
            <Input id="p-key" className="font-mono" value={field.key} onChange={(e) => set('key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} />
          </Field>
          <Field label="Übernehmen in Vorgang als" htmlFor="p-map">
            <Select id="p-map" value={field.mapsTo ?? ''} onChange={(e) => set('mapsTo', (e.target.value || undefined) as FormField['mapsTo'])}>
              <option value="">– nur in den Formularangaben –</option>
              {FIELD_MAPPINGS.map((m) => (
                <option key={m} value={m}>
                  {MAPPING_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
        </Section>
      )}
    </div>
  );
}

function PageProperties({ page, pageIndex, def, onChange, conditionFields }: { page: FormPage; pageIndex: number; def: FormDefinition; onChange: (p: FormPage) => void; conditionFields: FormField[] }) {
  const later = def.pages.slice(pageIndex + 1);
  const targetLabel = (id: string) => (id === SUBMIT ? 'Absenden' : def.pages.find((p) => p.id === id)?.title ?? '?');
  return (
    <div className="grid gap-4">
      <Section title="Seite">
        <Field label="Titel" htmlFor="pg-title">
          <Input id="pg-title" value={page.title} onChange={(e) => onChange({ ...page, title: e.target.value })} />
        </Field>
        <Field label="Beschreibung" htmlFor="pg-desc">
          <Textarea id="pg-desc" rows={2} value={page.description ?? ''} onChange={(e) => onChange({ ...page, description: e.target.value || undefined })} />
        </Field>
      </Section>
      <Section title="Verzweigung">
        <p className="text-xs text-muted-foreground">Die erste zutreffende Regel bestimmt, welche Seite als Nächstes kommt.</p>
        {page.branches.map((b, i) => (
          <div key={i} className="grid gap-2 rounded-md border p-2">
            <ConditionEditor
              value={b.when}
              fields={conditionFields}
              onChange={(v) => onChange({ ...page, branches: v ? page.branches.map((x, j) => (j === i ? { ...x, when: v } : x)) : page.branches.filter((_, j) => j !== i) })}
            />
            <div className="flex items-center gap-2 text-sm">
              <span className="shrink-0 text-muted-foreground">dann</span>
              <Select className="h-8" value={b.goTo} onChange={(e) => onChange({ ...page, branches: page.branches.map((x, j) => (j === i ? { ...x, goTo: e.target.value } : x)) })} aria-label="Zielseite">
                {later.map((p) => (
                  <option key={p.id} value={p.id}>
                    → {p.title}
                  </option>
                ))}
                <option value={SUBMIT}>→ Absenden</option>
              </Select>
            </div>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-fit"
          disabled={conditionFields.length === 0}
          onClick={() =>
            onChange({
              ...page,
              branches: [...page.branches, { when: { mode: 'all', conditions: [{ field: conditionFields[0]!.key, op: 'equals', value: conditionFields[0]!.options?.[0]?.value ?? '' }] }, goTo: later[0]?.id ?? SUBMIT }],
            })
          }
        >
          <GitBranch /> Regel hinzufügen
        </Button>
        <Field label="Sonst weiter mit" htmlFor="pg-next">
          <Select id="pg-next" value={page.defaultNext ?? ''} onChange={(e) => onChange({ ...page, defaultNext: e.target.value || undefined })}>
            <option value="">{later[0] ? `nächste Seite (${later[0].title})` : 'Absenden'}</option>
            {later.slice(1).map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
            {later.length > 0 && <option value={SUBMIT}>Absenden</option>}
          </Select>
        </Field>
        {page.branches.length > 0 && (
          <p className="text-xs text-muted-foreground">Ziele: {[...new Set(page.branches.map((b) => targetLabel(b.goTo)))].join(', ')}</p>
        )}
      </Section>
    </div>
  );
}

// --- Einstellungen ----------------------------------------------------------
function SettingsDialog({ form, def, open, onOpenChange, onSaved, onDefChange }: { form: FormDetail; def: FormDefinition; open: boolean; onOpenChange: (o: boolean) => void; onSaved: (f: FormDetail) => void; onDefChange: (d: FormDefinition) => void }) {
  const [s, setS] = useState(form);
  const [confirmation, setConfirmation] = useState(def.confirmation);
  const [submitLabel, setSubmitLabel] = useState(def.submitLabel);
  useEffect(() => {
    if (open) {
      setS(form);
      setConfirmation(def.confirmation);
      setSubmitLabel(def.submitLabel);
    }
  }, [open, form, def]);
  const { data: areas } = useQuery({ queryKey: ['ticket-config', 'areas'], queryFn: () => api<AreaConfig[]>('/ticket-config/areas') });
  const choiceFields = def.pages.flatMap((p) => p.fields).filter((f) => f.type === 'select' || f.type === 'radio');
  const routingField = choiceFields.find((f) => f.key === s.routing?.fieldKey);
  const save = useMutation({
    mutationFn: async () => {
      const saved = await api<FormDetail>(`/forms/${form.id}`, {
        method: 'PUT',
        body: { name: s.name, description: s.description, slug: s.slug, kind: s.kind, access: s.kind === 'CHECKLIST' ? 'INTERNAL' : s.access, active: s.active, areaId: s.areaId, routing: s.routing && Object.keys(s.routing.map).length ? s.routing : null, requireEmailConfirmation: s.requireEmailConfirmation },
      });
      return { saved, confirmation, submitLabel };
    },
    onSuccess: ({ saved, confirmation: c, submitLabel: l }) => {
      onSaved(saved);
      if (l !== def.submitLabel || c.title !== def.confirmation.title || c.message !== def.confirmation.message) {
        onDefChange({ ...def, submitLabel: l, confirmation: c });
      }
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const publicAreaHint = s.access === 'PUBLIC' && s.kind === 'REQUEST';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide>
        <DialogHeader>
          <DialogTitle>Formular-Einstellungen</DialogTitle>
        </DialogHeader>
        <DialogBody className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="s-name">
            <Input id="s-name" value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} />
          </Field>
          <Field label="Kürzel (Link /f/…)" htmlFor="s-slug">
            <Input id="s-slug" className="font-mono" value={s.slug} onChange={(e) => setS({ ...s, slug: e.target.value.toLowerCase() })} />
          </Field>
          <Field label="Beschreibung" htmlFor="s-desc" className="sm:col-span-2">
            <Textarea id="s-desc" rows={2} value={s.description ?? ''} onChange={(e) => setS({ ...s, description: e.target.value || null })} />
          </Field>
          <Field label="Art" htmlFor="s-kind">
            <Select id="s-kind" value={s.kind} onChange={(e) => setS({ ...s, kind: e.target.value as FormDetail['kind'] })}>
              <option value="REQUEST">Anfrage – erzeugt ein Ticket</option>
              <option value="CHECKLIST">Checkliste – wird an Ticket/Reparatur gehängt</option>
            </Select>
          </Field>
          {s.kind === 'REQUEST' && (
            <Field label="Wer kann das Formular nutzen?" htmlFor="s-access">
              <Select id="s-access" value={s.access} onChange={(e) => setS({ ...s, access: e.target.value as FormDetail['access'] })}>
                <option value="PUBLIC">Öffentlich (Link und Einbettung)</option>
                <option value="PORTAL">Nur angemeldete Kunden (Portal)</option>
                <option value="INTERNAL">Nur Mitarbeiter</option>
              </Select>
            </Field>
          )}
          {s.kind === 'REQUEST' && (
            <>
              <Field label="Ticketbereich" htmlFor="s-area" hint={publicAreaHint ? 'Öffentliche Formulare benötigen Bereiche mit Portalzugriff „Öffentlich“.' : undefined} className="sm:col-span-2">
                <Select id="s-area" value={s.areaId ?? ''} onChange={(e) => setS({ ...s, areaId: e.target.value || null })}>
                  <option value="">Allgemeine Anfrage (Standard)</option>
                  {areas?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} {publicAreaHint && a.portalAccess !== 'PUBLIC' ? '(nicht öffentlich)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="grid gap-2 sm:col-span-2">
                <Label>Bereich nach Antwort wählen (optional)</Label>
                <Select
                  value={s.routing?.fieldKey ?? ''}
                  onChange={(e) => setS({ ...s, routing: e.target.value ? { fieldKey: e.target.value, map: {} } : null })}
                  aria-label="Feld für Weiterleitung"
                >
                  <option value="">– keine Weiterleitung –</option>
                  {choiceFields.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                    </option>
                  ))}
                </Select>
                {routingField?.options?.map((o) => (
                  <div key={o.value} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
                    <span className="truncate">{o.label}</span>
                    <span className="text-muted-foreground">→</span>
                    <Select
                      className="h-8"
                      value={s.routing?.map[o.value] ?? ''}
                      onChange={(e) => {
                        const map = { ...(s.routing?.map ?? {}) };
                        if (e.target.value) map[o.value] = e.target.value;
                        else delete map[o.value];
                        setS({ ...s, routing: { fieldKey: routingField.key, map } });
                      }}
                      aria-label={`Bereich für ${o.label}`}
                    >
                      <option value="">Standardbereich</option>
                      {areas?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
              {s.access === 'PUBLIC' && (
                <label className="flex items-start gap-2 text-sm sm:col-span-2">
                  <Switch className="mt-0.5" checked={s.requireEmailConfirmation} onCheckedChange={(v) => setS({ ...s, requireEmailConfirmation: v })} />
                  <span>
                    E-Mail-Bestätigung verlangen
                    <span className="block text-xs text-muted-foreground">Empfohlen: Der Vorgang entsteht erst, wenn der Absender den Link in seiner E-Mail bestätigt (Schutz vor Spam und Tippfehlern).</span>
                  </span>
                </label>
              )}
            </>
          )}
          <Field label="Beschriftung der Absende-Schaltfläche" htmlFor="s-submit">
            <Input id="s-submit" value={submitLabel} onChange={(e) => setSubmitLabel(e.target.value)} />
          </Field>
          <label className="flex items-center gap-2 self-end text-sm">
            <Switch checked={s.active} onCheckedChange={(v) => setS({ ...s, active: v })} /> Formular aktiv
          </label>
          <Field label="Titel der Bestätigungsseite" htmlFor="s-ctitle" className="sm:col-span-2">
            <Input id="s-ctitle" value={confirmation.title} onChange={(e) => setConfirmation({ ...confirmation, title: e.target.value })} />
          </Field>
          <Field label="Text der Bestätigungsseite" htmlFor="s-cmsg" className="sm:col-span-2">
            <Textarea id="s-cmsg" rows={3} value={confirmation.message} onChange={(e) => setConfirmation({ ...confirmation, message: e.target.value })} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Builder -----------------------------------------------------------------
export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [confirmDialog, confirm] = useConfirm();
  const { data: form } = useQuery({ queryKey: ['forms', id], queryFn: () => api<FormDetail>(`/forms/${id}`) });
  const [def, setDef] = useState<FormDefinition | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [settings, setSettings] = useState(false);
  const [embed, setEmbed] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  useEffect(() => {
    if (form && !def) setDef(form.draft as FormDefinition);
  }, [form, def]);

  const navigate = useNavigate();
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const update = (next: FormDefinition) => {
    setDef(next);
    setDirty(true);
  };

  const allFields = useMemo(() => def?.pages.flatMap((p) => p.fields) ?? [], [def]);
  const inputs = allFields.filter((f) => !STATIC_FIELD_TYPES.includes(f.type));
  const keys = new Set(inputs.map((f) => f.key));

  const onSaved = (f: FormDetail) => {
    qc.setQueryData(['forms', id], f);
    qc.invalidateQueries({ queryKey: ['forms'], exact: true });
  };
  const handleError = (e: unknown) => {
    if (e instanceof ApiError && e.details) setErrors(e.details.map((d) => d.message));
    else setErrors([]);
    toast.error(errorMessage(e).split(' pages.')[0]!);
  };
  const saveDraft = useMutation({
    mutationFn: () => api<FormDetail>(`/forms/${id}/draft`, { method: 'PUT', body: { definition: def } }),
    onSuccess: (f) => {
      onSaved(f);
      setDirty(false);
      setErrors([]);
      toast.success('Entwurf gespeichert');
    },
    onError: handleError,
  });
  const publish = useMutation({
    mutationFn: async () => {
      if (dirty) await api(`/forms/${id}/draft`, { method: 'PUT', body: { definition: def } });
      return api<FormDetail>(`/forms/${id}/publish`, { method: 'POST' });
    },
    onSuccess: (f) => {
      onSaved(f);
      setDirty(false);
      setErrors([]);
      toast.success(`Version ${f.publishedVersion?.version} veröffentlicht`);
    },
    onError: handleError,
  });
  const restore = useMutation({
    mutationFn: (versionId: string) => api<FormDetail>(`/forms/${id}/versions/${versionId}/restore`, { method: 'POST' }),
    onSuccess: (f) => {
      onSaved(f);
      setDef(f.draft as FormDefinition);
      setDirty(false);
      setSelectedId(null);
      setPageIndex(0);
      toast.success('Version als Entwurf geladen');
    },
    onError: handleError,
  });

  if (!form || !def) return <Skeleton className="h-[70vh]" />;
  const page = def.pages[pageIndex] ?? def.pages[0]!;
  const selected = page.fields.find((f) => f.id === selectedId) ?? null;

  const setPage = (p: FormPage) => update({ ...def, pages: def.pages.map((x, i) => (i === pageIndex ? p : x)) });
  const setFields = (fields: FormField[]) => setPage({ ...page, fields });
  const insertField = (type: FieldType, at?: number) => {
    const f = newField(type, keys);
    const fields = [...page.fields];
    fields.splice(at ?? fields.length, 0, f);
    setFields(fields);
    setSelectedId(f.id);
  };
  const moveField = (from: number, to: number) => {
    if (to < 0 || to >= page.fields.length + 1 || from === to) return;
    const fields = [...page.fields];
    const [f] = fields.splice(from, 1);
    fields.splice(to > from ? to - 1 : to, 0, f!);
    setFields(fields);
  };
  const removeField = (fid: string) => {
    setFields(page.fields.filter((f) => f.id !== fid));
    if (selectedId === fid) setSelectedId(null);
  };
  const duplicateField = (f: FormField) => {
    const copy = { ...f, id: rid('f_'), key: STATIC_FIELD_TYPES.includes(f.type) ? f.key : toKey(f.key, keys), label: `${f.label} (Kopie)` };
    const i = page.fields.findIndex((x) => x.id === f.id);
    const fields = [...page.fields];
    fields.splice(i + 1, 0, copy);
    setFields(fields);
    setSelectedId(copy.id);
  };
  const addPage = () => {
    const p: FormPage = { id: rid('p_'), title: `Seite ${def.pages.length + 1}`, fields: [], branches: [] };
    update({ ...def, pages: [...def.pages, p] });
    setPageIndex(def.pages.length);
    setSelectedId(null);
  };
  const movePage = (from: number, to: number) => {
    if (to < 0 || to >= def.pages.length) return;
    const pages = [...def.pages];
    const [p] = pages.splice(from, 1);
    pages.splice(to, 0, p!);
    update({ ...def, pages });
    setPageIndex(to);
  };
  const removePage = async (i: number) => {
    if (def.pages.length === 1) return;
    const p = def.pages[i]!;
    if (p.fields.length && !(await confirm({ title: `Seite „${p.title}“ mit ${p.fields.length} Feldern löschen?`, confirmLabel: 'Löschen', destructive: true }))) return;
    const pages = def.pages
      .filter((_, j) => j !== i)
      .map((x) => ({ ...x, branches: x.branches.filter((b) => b.goTo !== p.id), defaultNext: x.defaultNext === p.id ? undefined : x.defaultNext }));
    update({ ...def, pages });
    setPageIndex(Math.max(0, i - 1));
    setSelectedId(null);
  };

  const onDrop = (e: DragEvent, at: number) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/x-field-type') as FieldType;
    if (type) insertField(type, at);
    else if (dragIndex !== null) moveField(dragIndex, at);
    setDragIndex(null);
    setDropIndex(null);
  };
  const DropLine = ({ at }: { at: number }) => (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDropIndex(at);
      }}
      onDragLeave={() => setDropIndex((d) => (d === at ? null : d))}
      onDrop={(e) => onDrop(e, at)}
      className={cn('-my-1 h-3 rounded transition-colors', dropIndex === at && 'h-8 border-2 border-dashed border-primary bg-primary/5')}
    />
  );

  return (
    <>
      {confirmDialog}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <Link
            to="/forms"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            onClick={async (e) => {
              if (!dirty) return;
              e.preventDefault();
              if (await confirm({ title: 'Ungespeicherte Änderungen verwerfen?', confirmLabel: 'Verwerfen', destructive: true })) navigate('/forms');
            }}
          >
            <ArrowLeft className="size-4" /> Formulare
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-2xl font-semibold tracking-tight">
            {form.name}
            <Badge variant="outline">{ACCESS_LABELS[form.access]}</Badge>
            {form.publishedVersion ? <Badge variant="success">Version {form.publishedVersion.version} veröffentlicht</Badge> : <Badge variant="warning">nicht veröffentlicht</Badge>}
            {(dirty || (form.publishedVersion && form.hasUnpublishedChanges)) && <Badge variant="secondary">{dirty ? 'ungespeichert' : 'Entwurf weicht ab'}</Badge>}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setSettings(true)}>
            <Settings /> Einstellungen
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreview(true)}>
            <Eye /> Vorschau
          </Button>
          {form.versions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <History /> Versionen <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Als Entwurf wiederherstellen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {form.versions.map((v) => (
                  <DropdownMenuItem
                    key={v.id}
                    onSelect={async () => {
                      if (!dirty || (await confirm({ title: 'Ungespeicherte Änderungen verwerfen?', confirmLabel: 'Verwerfen', destructive: true }))) restore.mutate(v.id);
                    }}
                  >
                    Version {v.version} · {formatDateTime(v.publishedAt)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {form.access === 'PUBLIC' && form.publishedVersion && (
            <Button variant="outline" size="sm" onClick={() => setEmbed(true)}>
              <Code2 /> Einbetten
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => saveDraft.mutate()} disabled={!dirty} loading={saveDraft.isPending}>
            Entwurf speichern
          </Button>
          <Button size="sm" onClick={() => publish.mutate()} loading={publish.isPending}>
            Veröffentlichen
          </Button>
        </div>
      </div>

      {errors.length > 0 && (
        <Alert variant="destructive" className="mb-4">
          <ul className="list-inside list-disc">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)_20rem]">
        {/* Seiten und Feldpalette */}
        <div className="flex flex-col gap-4">
          <Card className="p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Seiten</p>
            <ol className="flex flex-col gap-1">
              {def.pages.map((p, i) => (
                <li key={p.id} className={cn('group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm', i === pageIndex ? 'bg-primary/10 text-primary' : 'hover:bg-accent')}>
                  <button className="min-w-0 flex-1 truncate text-left" onClick={() => { setPageIndex(i); setSelectedId(null); }}>
                    {i + 1}. {p.title}
                    {p.branches.length > 0 && <GitBranch className="ml-1 inline size-3" aria-label="mit Verzweigung" />}
                  </button>
                  <span className="hidden gap-0.5 group-hover:flex">
                    <button onClick={() => movePage(i, i - 1)} aria-label="Seite nach oben" className="text-muted-foreground hover:text-foreground">
                      <ArrowUp className="size-3.5" />
                    </button>
                    <button onClick={() => movePage(i, i + 1)} aria-label="Seite nach unten" className="text-muted-foreground hover:text-foreground">
                      <ArrowDown className="size-3.5" />
                    </button>
                    {def.pages.length > 1 && (
                      <button onClick={() => removePage(i)} aria-label="Seite löschen" className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="size-3.5" />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ol>
            <Button variant="ghost" size="sm" className="mt-1 w-full" onClick={addPage}>
              <Plus /> Seite
            </Button>
          </Card>
          <Card className="p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Felder</p>
            <p className="mb-2 text-xs text-muted-foreground">Klicken oder in die Seite ziehen</p>
            <div className="grid grid-cols-2 gap-1.5 lg:grid-cols-1">
              {PALETTE.map((p) => (
                <button
                  key={p.type}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('application/x-field-type', p.type)}
                  onClick={() => insertField(p.type)}
                  className="flex cursor-grab items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left text-sm hover:border-primary/40 hover:bg-accent active:cursor-grabbing"
                >
                  <p.icon className="size-4 text-muted-foreground" /> {p.label}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Arbeitsfläche */}
        <Card className="min-h-[60vh] p-4 sm:p-6" onClick={() => setSelectedId(null)}>
          <button className="mb-1 block w-full text-left" onClick={(e) => { e.stopPropagation(); setSelectedId(null); }}>
            <p className="text-xs text-muted-foreground">Seite {pageIndex + 1} von {def.pages.length}</p>
            <h2 className="text-lg font-semibold">{page.title}</h2>
            {page.description && <p className="text-sm text-muted-foreground">{page.description}</p>}
          </button>
          <div className="mt-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <DropLine at={0} />
            {page.fields.length === 0 && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, 0)}
                className="flex h-32 items-center justify-center rounded-lg border-2 border-dashed text-sm text-muted-foreground"
              >
                Felder hierher ziehen oder links anklicken
              </div>
            )}
            {page.fields.map((f, i) => {
              const Icon = TYPE_ICON[f.type];
              return (
                <div key={f.id}>
                  <div
                    draggable
                    onDragStart={() => setDragIndex(i)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDropIndex(null);
                    }}
                    onClick={() => setSelectedId(f.id)}
                    className={cn(
                      'group flex items-start gap-2 rounded-lg border bg-card p-3 transition-colors',
                      selectedId === f.id ? 'border-primary ring-2 ring-primary/20' : 'hover:border-primary/40',
                      dragIndex === i && 'opacity-40',
                      f.width === 'half' && 'sm:w-1/2',
                    )}
                  >
                    <GripVertical className="mt-0.5 size-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                        <Icon className="size-3.5 text-muted-foreground" />
                        <span className="truncate">{f.label}</span>
                        {f.required && <span className="text-destructive">*</span>}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {!STATIC_FIELD_TYPES.includes(f.type) && <span className="font-mono text-[11px] text-muted-foreground">{f.key}</span>}
                        {f.visibleIf && <Badge variant="outline" className="text-[11px]">bedingt sichtbar</Badge>}
                        {f.mapsTo && <Badge variant="secondary" className="text-[11px]">→ {MAPPING_LABELS[f.mapsTo]}</Badge>}
                        {f.options && <span className="text-[11px] text-muted-foreground">{f.options.map((o) => o.label).join(' · ')}</span>}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-0.5 opacity-60 group-hover:opacity-100">
                      <button onClick={(e) => { e.stopPropagation(); moveField(i, i - 1); }} aria-label="Nach oben" className="rounded p-1 hover:bg-accent">
                        <ArrowUp className="size-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveField(i, i + 2); }} aria-label="Nach unten" className="rounded p-1 hover:bg-accent">
                        <ArrowDown className="size-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); duplicateField(f); }} aria-label="Duplizieren" className="rounded p-1 hover:bg-accent">
                        <Copy className="size-3.5" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeField(f.id); }} aria-label="Löschen" className="rounded p-1 hover:bg-accent hover:text-destructive">
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </div>
                  <DropLine at={i + 1} />
                </div>
              );
            })}
          </div>
          {pageIndex < def.pages.length - 1 && page.branches.length === 0 && !page.defaultNext && (
            <p className="mt-4 text-xs text-muted-foreground">Danach: Seite {pageIndex + 2} „{def.pages[pageIndex + 1]!.title}“</p>
          )}
        </Card>

        {/* Eigenschaften */}
        <Card className="h-fit p-4 lg:sticky lg:top-20">
          {selected ? (
            <FieldProperties
              key={selected.id}
              field={selected}
              allKeys={new Set(inputs.filter((x) => x.id !== selected.id).map((x) => x.key))}
              conditionFields={inputs}
              onChange={(f) => setFields(page.fields.map((x) => (x.id === f.id ? f : x)))}
            />
          ) : (
            <PageProperties key={page.id} page={page} pageIndex={pageIndex} def={def} onChange={setPage} conditionFields={inputs} />
          )}
        </Card>
      </div>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent wide>
          <DialogHeader>
            <DialogTitle>Vorschau: {form.name}</DialogTitle>
            <DialogDescription>So sieht das Formular mit dem aktuellen Entwurf aus. Absenden schickt nichts ab.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            {preview && (
              <FormRenderer
                key={JSON.stringify(def).length}
                definition={def}
                onSubmit={async (data) => {
                  toast.info(`Vorschau – nicht gesendet. ${Object.keys(data).length} Antworten erfasst.`);
                  setPreview(false);
                }}
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
      <SettingsDialog
        form={form}
        def={def}
        open={settings}
        onOpenChange={setSettings}
        onSaved={(f) => onSaved(f)}
        onDefChange={update}
      />
      {embed && <EmbedDialog form={form} open onOpenChange={setEmbed} />}
    </>
  );
}
